import { createRootRoute, Link, Outlet, useLocation, useRouter } from '@tanstack/react-router';
import {
  LayoutDashboard,
  Building2,
  Package,
  MessageSquare,
  MessageCircle,
  ClipboardCheck,
  BarChart3,
  Settings,
  LogOut,
  Menu,
  User,
  Users,
  FileText,
  AlertTriangle,
  Home,
  Monitor,
  Cpu,
  ThumbsUp,
  Lightbulb,
  Sparkles,
  DatabaseBackup,
  ServerCog,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiService } from '@/core/api/service';
import { useAuth } from '@/core/auth-context';
import { useAppState } from '@/core/app-context';
import { useUtmParams } from '@/core/use-utm-params';
import { isLocalBackend } from '@/core/api/client';
import { LoginScreen } from '@/features/auth/login-screen';
import { TenantSelector } from '@/features/tenant-selector';
import { BrandLogo } from '@/components/BrandLogo';
import { ThemeToggle } from '@/components/ThemeToggle';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';

const navItems = [
  { to: '/', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/tenants', label: 'Tenant', icon: Building2 },
  { to: '/sector-config', label: 'Sector Config', icon: Sparkles },
  { to: '/users', label: 'Utenze Chatbot', icon: Users },
  { to: '/backoffice-users', label: 'Utenze Backoffice', icon: User },
  { to: '/conversations', label: 'Conversazioni', icon: MessageCircle },
  { to: '/feedback', label: 'Feedback', icon: ThumbsUp },
  { to: '/user-reports', label: 'Segnalazioni', icon: Lightbulb },
  { to: '/products', label: 'Prodotti', icon: Package },
  { to: '/llm-models', label: 'Modelli LLM', icon: Cpu },
  { to: '/chat-test', label: 'Chat Test', icon: MessageSquare },
  { to: '/battery-test', label: 'Batterie Test', icon: ClipboardCheck },
  { to: '/analytics', label: 'Analytics', icon: BarChart3 },
  { to: '/logs', label: 'Log', icon: FileText },
  { to: '/system', label: 'Sistema', icon: Monitor },
  { to: '/backups', label: 'Backup', icon: DatabaseBackup },
  { to: '/staging', label: 'Staging', icon: ServerCog },
  { to: '/settings', label: 'Settings', icon: Settings },
];

function NotFoundComponent() {
  return (
    <div className="flex min-h-[calc(100vh-4rem)] flex-col items-center justify-center bg-background px-4 text-center animate-fadeIn">
      <div className="h-20 w-20 rounded-3xl bg-surface-2 flex items-center justify-center mb-6">
        <AlertTriangle className="h-10 w-10 text-destructive" />
      </div>
      <h1 className="text-7xl font-bold text-foreground">404</h1>
      <h2 className="mt-4 text-xl font-semibold text-foreground">Pagina non trovata</h2>
      <p className="mt-2 text-sm text-muted-foreground max-w-xs">
        La pagina che stai cercando non esiste o è stata spostata.
      </p>
      <div className="mt-6">
        <Link
          to="/"
          className="inline-flex items-center gap-2 rounded-xl bg-brand px-5 py-2.5 text-sm font-semibold text-brand-foreground transition-colors hover:bg-brand-glow"
        >
          <Home className="h-4 w-4" />
          Torna alla dashboard
        </Link>
      </div>
    </div>
  );
}

function ErrorFallback({ error, reset }: { error: Error; reset: () => void }) {
  const router = useRouter();

  useEffect(() => {
    console.error(error);
  }, [error]);

  return (
    <div className="flex min-h-[calc(100vh-4rem)] flex-col items-center justify-center bg-background px-4 py-10 text-center">
      <div className="h-20 w-20 rounded-3xl bg-destructive/10 flex items-center justify-center mb-6">
        <AlertTriangle className="h-10 w-10 text-destructive" />
      </div>
      <h1 className="text-xl font-semibold tracking-tight text-foreground">Questa pagina non si è caricata</h1>
      <p className="mt-2 text-sm text-muted-foreground max-w-md">
        Qualcosa è andato storto. Puoi riprovare o tornare alla dashboard.
      </p>
      <div className="mt-2 max-w-xl w-full text-left">
        <details className="rounded-2xl border border-border bg-surface p-3">
          <summary className="cursor-pointer text-xs font-semibold text-foreground">
            Dettagli errore
          </summary>
          <p className="mt-2 break-words text-xs text-destructive">{error.message}</p>
          {error.stack ? (
            <pre className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap break-words text-[10px] text-muted-foreground">
              {error.stack}
            </pre>
          ) : null}
        </details>
      </div>
      <div className="mt-6 flex flex-wrap justify-center gap-2">
        <Button
          onClick={() => {
            router.invalidate();
            reset();
          }}
          className="rounded-xl bg-brand hover:bg-brand-glow text-brand-foreground"
        >
          Riprova
        </Button>
        <Link
          to="/"
          className="inline-flex items-center justify-center rounded-xl border border-border bg-surface px-4 py-2 text-sm font-semibold text-foreground transition-colors hover:bg-surface-2"
        >
          Torna alla dashboard
        </Link>
      </div>
    </div>
  );
}

/**
 * Segnalazioni in stato "new" del tenant selezionato: è il solo numero che
 * chiede un'azione, quindi l'unico che merita un pallino nel menu. La query è
 * condivisa tra le due nav dalla cache di react-query.
 */
function useNewReportsCount(): number {
  const { isAuthenticated } = useAuth();
  const { selectedTenant } = useAppState();
  const tenantId = selectedTenant?.tenantId;
  const { data } = useQuery({
    queryKey: ['userReportStats', tenantId],
    queryFn: () => apiService.getUserReportStats(tenantId),
    enabled: isAuthenticated && !!tenantId,
    staleTime: 1000 * 60,
  });
  return data?.byStatus.new ?? 0;
}

function NavBadge({ count }: { count: number }) {
  if (count <= 0) return null;
  return (
    <span className="ml-auto inline-flex min-w-[20px] items-center justify-center rounded-full bg-brand/15 px-1.5 text-[11px] font-bold tabular-nums text-brand">
      {count > 99 ? '99+' : count}
    </span>
  );
}

function MobileNav({ close }: { close: () => void }) {
  const location = useLocation();
  const newReports = useNewReportsCount();
  return (
    <nav className="flex flex-col gap-1 mt-4 overflow-y-auto">
      {navItems.map((item) => {
        const active = location.pathname === item.to || location.pathname.startsWith(`${item.to}/`);
        return (
          <Link
            key={item.to}
            to={item.to}
            onClick={close}
            className={cn(
              'flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold transition-colors',
              active
                ? 'bg-brand text-brand-foreground'
                : 'text-muted-foreground hover:bg-surface-2 hover:text-foreground',
            )}
          >
            <item.icon className="h-5 w-5 shrink-0" />
            <span>{item.label}</span>
            {item.to === '/user-reports' && <NavBadge count={newReports} />}
          </Link>
        );
      })}
    </nav>
  );
}

function DesktopNav() {
  const location = useLocation();
  const newReports = useNewReportsCount();
  return (
    <aside className="hidden md:flex w-60 flex-col gap-1 border-r bg-surface p-3 overflow-y-auto">
      <div className="mb-2 px-3 py-2">
        <BrandLogo size="md" />
      </div>
      {navItems.map((item) => {
        const active = location.pathname === item.to || location.pathname.startsWith(`${item.to}/`);
        return (
          <Link
            key={item.to}
            to={item.to}
            className={cn(
              'flex items-center gap-3 rounded-xl px-3 py-2 text-sm font-semibold transition-colors',
              active
                ? 'bg-brand text-brand-foreground shadow-glow-sm'
                : 'text-muted-foreground hover:bg-surface-2 hover:text-foreground',
            )}
          >
            <item.icon className="h-5 w-5 shrink-0" />
            <span>{item.label}</span>
            {item.to === '/user-reports' && <NavBadge count={newReports} />}
          </Link>
        );
      })}
    </aside>
  );
}

function UserMenu() {
  const { username, adminApiKey, logout } = useAuth();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="rounded-xl">
          <User className="h-5 w-5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>
          <div className="flex flex-col">
            <span className="truncate">{username}</span>
            <span className="text-xs font-normal text-muted-foreground truncate">
              {adminApiKey ? 'API key configurata' : 'API key non configurata'}
            </span>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to="/settings" className="cursor-pointer">
            <Settings className="mr-2 h-4 w-4" />
            Impostazioni
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={logout} className="cursor-pointer text-destructive focus:text-destructive">
          <LogOut className="mr-2 h-4 w-4" />
          Logout
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function DevBanner() {
  if (!isLocalBackend) return null;
  const message =
    'ATTENZIONE: stai usando il backend di sviluppo locale — i dati potrebbero non essere quelli di produzione.';
  return (
    <div className="pointer-events-none fixed right-0 top-0 z-50 h-24 w-24 overflow-hidden">
      <div
        className="pointer-events-auto absolute right-[-34px] top-[18px] w-36 rotate-45 bg-amber-500 py-1 text-center text-[10px] font-bold text-white shadow-md"
        title={message}
      >
        DEV
      </div>
    </div>
  );
}

function RootLayout() {
  const { isAuthenticated } = useAuth();
  const { selectedTenant } = useAppState();
  const utm = useUtmParams();
  const hasUtm = Object.values(utm).some((v) => v);
  const [mobileOpen, setMobileOpen] = useState(false);

  if (!isAuthenticated) {
    return (
      <>
        <DevBanner />
        <LoginScreen />
      </>
    );
  }

  return (
    <div className="flex h-screen flex-col bg-background">
      <DevBanner />
      <header className="flex h-16 items-center justify-between border-b bg-surface/80 backdrop-blur-xl px-4 md:px-6">
        <div className="flex items-center gap-3 md:hidden">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="rounded-xl">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-72 bg-surface p-4">
              <SheetHeader>
                <SheetTitle>
                  <BrandLogo size="md" />
                </SheetTitle>
              </SheetHeader>
              <MobileNav close={() => setMobileOpen(false)} />
            </SheetContent>
          </Sheet>
          <BrandLogo size="sm" />
        </div>

        <div className="hidden md:flex items-center gap-4">
          <BrandLogo size="md" />
          {selectedTenant && (
            <span className="text-sm text-muted-foreground">
              {selectedTenant.name}
            </span>
          )}
        </div>

        <div className="flex items-center gap-2 md:gap-4">
          <TenantSelector />
          <ThemeToggle />
          <UserMenu />
        </div>
      </header>

      {hasUtm && (
        <div className="flex flex-wrap gap-2 border-b bg-surface-2 px-4 py-2 text-xs">
          {utm.source && <span className="rounded-lg bg-background px-2 py-1">source: {utm.source}</span>}
          {utm.medium && <span className="rounded-lg bg-background px-2 py-1">medium: {utm.medium}</span>}
          {utm.campaign && <span className="rounded-lg bg-background px-2 py-1">campaign: {utm.campaign}</span>}
          {utm.content && <span className="rounded-lg bg-background px-2 py-1">content: {utm.content}</span>}
          {utm.term && <span className="rounded-lg bg-background px-2 py-1">term: {utm.term}</span>}
        </div>
      )}

      <div className="flex flex-1 overflow-hidden">
        <DesktopNav />
        <main className="flex-1 overflow-auto p-4 md:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

export const Route = createRootRoute({
  component: RootLayout,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorFallback,
  head: () => ({
    meta: [
      { name: 'robots', content: 'noindex, nofollow' },
      { name: 'googlebot', content: 'noindex, nofollow' },
    ],
  }),
});
